import { cva } from 'class-variance-authority'

// TODO[missing-item-reflow-threshold-token]: define a shared threshold token for Item Details after visual approval.

/**
 * Layout for the structural row wrapper and its primary link.
 * The link spans the intrinsic action track, so its stretched hit area covers the row while Actions paints above it.
 *
 * Column tracks, by ControlItem control position:
 *
 *                      1         2          3          4         5          6
 *   no control         media     content*   details    actions   indicator
 *   leading control    control   media      content*   details   actions    indicator
 *   trailing control   media     content*   details    actions   indicator  control
 *
 *   * `minmax(0, 1fr)`: the only track that grows or shrinks. Every other track is `auto`, so an absent part takes no
 *     space.
 *
 * Actions sit directly in this grid. Every other part sits in the primary element, a subgrid that spans all the tracks.
 */
export const itemRootFrameVariants = cva(
  `
    @container/item-root relative isolate grid w-full grid-cols-[auto_minmax(0,1fr)_auto_auto_auto]
    grid-rows-[auto_auto] items-stretch
    [&:has(>[data-slot=item]>[data-control-item-position=leading])]:grid-cols-[auto_auto_minmax(0,1fr)_auto_auto_auto]
    [&:has(>[data-slot=item]>[data-control-item-position=leading])>[data-slot=item-actions]]:col-start-5
    [&:has(>[data-slot=item]>[data-control-item-position=trailing])]:grid-cols-[auto_minmax(0,1fr)_auto_auto_auto_auto]
    [&>[data-slot=item-actions]]:relative [&>[data-slot=item-actions]]:col-start-4
    [&>[data-slot=item-actions]]:row-span-full [&>[data-slot=item-actions]]:ml-0
  `,
)

/**
 * Places each part on the frame's column tracks (see `itemRootFrameVariants`), shown here without a control. A leading
 * control shifts every part one track to the right.
 *
 * Container 640px or wider: every part spans both rows.
 *
 *   ┌───────┬────────────────┬─────────┬─────────┬───────────┐
 *   │ media │ content        │ details │ actions │ indicator │
 *   └───────┴────────────────┴─────────┴─────────┴───────────┘
 *
 * Narrower than 640px: details move to row 2, under the content, and also take the details track.
 *
 *   ┌───────┬──────────────────────────┬─────────┬───────────┐
 *   │ media │ content                  │ actions │ indicator │  row 1
 *   │       ├──────────────────────────┤         │           │
 *   │       │ details                  │         │           │  row 2
 *   └───────┴──────────────────────────┴─────────┴───────────┘
 *
 * Parts are matched as descendants, not children: an element passed to `render` may wrap them in a `display: contents`
 * element (ClickGuard does), which keeps them grid items but hides them from a child selector.
 */
export const itemRootPrimaryVariants = cva(
  `
    relative col-span-full row-span-full grid min-w-0 grid-cols-subgrid grid-rows-subgrid items-center
    @max-[640px]/item-root:[grid-template-rows:auto_auto]
    [&_[data-slot=item-content]]:col-start-2 [&_[data-slot=item-content]]:row-span-full
    [&_[data-slot=item-content]]:min-w-0
    [&_[data-slot=item-details]]:col-start-3 [&_[data-slot=item-details]]:row-span-full
    @max-[640px]/item-root:[&_[data-slot=item-details]]:col-[2/4]
    @max-[640px]/item-root:[&_[data-slot=item-details]]:row-start-2
    [&_[data-slot=item-indicator]]:col-start-5 [&_[data-slot=item-indicator]]:row-span-full
    [&_[data-slot=item-media]]:col-start-1 [&_[data-slot=item-media]]:row-span-full
    [&:has(>[data-control-item-position=leading])_[data-slot=item-content]]:col-start-3
    [&:has(>[data-control-item-position=leading])_[data-slot=item-details]]:col-start-4
    @max-[640px]/item-root:[&:has(>[data-control-item-position=leading])_[data-slot=item-details]]:col-[3/5]
    [&:has(>[data-control-item-position=leading])_[data-slot=item-indicator]]:col-start-6
    [&:has(>[data-control-item-position=leading])_[data-slot=item-media]]:col-start-2
    @max-[640px]/item-root:[&:has([data-slot=item-details])_[data-slot=item-content]]:row-span-1
  `,
)
